import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useEffect, useMemo } from 'react';
import {
  Link,
  Outlet,
  useLocation,
  useOutletContext,
  useParams
} from 'react-router';

import { SIDEBAR_MENU_ITEMS } from './constants';
import { type ContextType } from './useOutletContext';

import type { AdditionalBreadcrumbItem } from '@/components/Layout';
import {
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from '@/components/ui/sidebar';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from '@/components/ui/tooltip';
import { useRecentItems } from '@/hooks/useRecentItems';
import { cn } from '@/lib/utils';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AssertIsDisabledCallable = (...args: any[]) => boolean;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BadgeRenderer = (...args: any[]) => React.ReactNode;

interface GetDealOpportunity {
  node: {
    __typename: 'OpportunityNode';
    closingDate: null | string;
    icVoteTimestamp: null | string;
    id: string;
    name: string;
    projectName: null | string;
    stage: null | string;
    stageDisplay: null | string;
    status: null | string;
    statusDisplay: null | string;
    targetEntityId: {
      __typename: 'AccountNode';
      id: string;
      name: string;
    } | null;
  };
}

interface GetDealOpportunityDueDiligence {
  node: {
    __typename: 'OpportunityNode';
    dueDiligenceProjectId: {
      nextMilestoneId: {
        id: string;
        isReached: boolean;
        name: string;
      } | null;
      rootTasks: {
        childIds:
          | {
              id: string;
            }[]
          | null;
        subtaskCompletionPercentage: number;
      }[];
    } | null;
  };
}

const GET_DEAL_OPPORTUNITY = gql`
  query DealOpportunity($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        name
        closingDate
        projectName
        icVoteTimestamp
        status
        statusDisplay
        stage
        stageDisplay
        targetEntityId {
          ... on AccountNode {
            id
            name
          }
        }
      }
    }
  }
`;

const GET_DEAL_OPPORTUNITY_DUE_DILIGENCE = gql`
  query DealOpportunityDueDiligence($id: ID!) {
    node(id: $id) {
      ... on OpportunityNode {
        id
        name
        dueDiligenceProjectId {
          id
          name
          nextMilestoneId {
            id
            name
            isReached
          }
          rootTasks {
            id
            name
            completionDate
            subtaskCompletionPercentage
            startDay
            endDay
            startDateClosingDate
            endDateClosingDate
            milestoneId {
              id
              name
              isReached
              reachedDate
              inputReachedDate
            }
            childIds {
              id
              name
              dateEnd
              completionDate
              state
              startDay
              endDay
              startDateClosingDate
              endDateClosingDate
            }
          }
        }
      }
    }
  }
`;

const InvestmentsDirectDetails = () => {
  const { pathname } = useLocation();
  const { addRecentItem } = useRecentItems();

  const { id } = useParams<{ id: string }>();

  const parentContext = useOutletContext<{
    addBreadcrumb?: (item: { label: string; to?: string }) => void;
    additionalBreadcrumbs: AdditionalBreadcrumbItem[];
  }>();

  if (!id) {
    throw new Error();
  }

  const { data, loading } = useQuery<GetDealOpportunity, { id: string }>(
    GET_DEAL_OPPORTUNITY,
    {
      variables: { id }
    }
  );

  // Track this opportunity as recently viewed
  useEffect(() => {
    if (data?.node && id) {
      addRecentItem({
        id,
        metadata: {
          stage: data.node.stageDisplay ?? data.node.stage ?? undefined,
          status: data.node.statusDisplay ?? data.node.status ?? undefined
        },
        name: data.node.name || 'Unnamed Opportunity',
        type: 'opportunity'
      });
    }
  }, [data, id, addRecentItem]);

  const { data: dueDiligenceData } = useQuery<
    GetDealOpportunityDueDiligence,
    { id: string }
  >(GET_DEAL_OPPORTUNITY_DUE_DILIGENCE, {
    variables: { id }
  });

  const completedDdTasksCount = useMemo(() => {
    if (!dueDiligenceData?.node.dueDiligenceProjectId?.rootTasks) {
      return 0;
    }
    return dueDiligenceData.node.dueDiligenceProjectId.rootTasks.reduce(
      (total, rootTask) => {
        return total + Math.round(rootTask.subtaskCompletionPercentage);
      },
      0
    );
  }, [dueDiligenceData]);

  const { addBreadcrumb, additionalBreadcrumbs } = parentContext;

  const renderedBadge = (
    name: string,
    badge: BadgeRenderer | React.JSX.Element | undefined
  ) => {
    if (!badge) {
      return null;
    }
    if (typeof badge !== 'function') {
      return badge;
    }
    if (name === 'Due Diligence') {
      return badge(completedDdTasksCount);
    }
    return null;
  };
  const AssertIsDisabled = (
    name: string,
    isDisabled?: AssertIsDisabledCallable | null
  ) => {
    if (!isDisabled) {
      return false;
    }
    switch (name) {
      case 'Due Diligence':
        return isDisabled(!!dueDiligenceData?.node.dueDiligenceProjectId);
      default:
        return false;
    }
  };
  return (
    <div className="flex h-[calc(100dvh-64px)] flex-col md:grid md:grid-cols-[210px_1fr]">
      <nav className="bg-sidebar shrink-0 border-b md:hidden">
        <div className="flex gap-1 overflow-x-auto px-2 py-2">
          {SIDEBAR_MENU_ITEMS.map(
            ({ badge, isDisabled, isDisabledTooltip, name, url }) => {
              const disabled = AssertIsDisabled(name, isDisabled);
              const showTooltip =
                disabled &&
                typeof isDisabledTooltip === 'string' &&
                isDisabledTooltip.trim() !== '';
              const isActive = pathname.split('/')[4] === url;

              const content = (
                <span className="flex items-center gap-1.5">
                  {name}
                  {renderedBadge(name, badge)}
                </span>
              );

              if (showTooltip) {
                return (
                  <Tooltip key={name}>
                    <TooltipTrigger asChild>
                      <button
                        className="text-muted-foreground inline-flex shrink-0 items-center rounded-md px-3 py-1.5 text-sm font-medium opacity-50"
                        disabled
                        type="button"
                      >
                        {content}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      {typeof isDisabledTooltip === 'string'
                        ? isDisabledTooltip
                        : ''}
                    </TooltipContent>
                  </Tooltip>
                );
              }

              if (disabled) {
                return (
                  <button
                    className="text-muted-foreground inline-flex shrink-0 items-center rounded-md px-3 py-1.5 text-sm font-medium opacity-50"
                    disabled
                    key={name}
                    type="button"
                  >
                    {content}
                  </button>
                );
              }

              return (
                <Link
                  className={cn(
                    'inline-flex shrink-0 items-center rounded-md px-3 py-1.5 text-sm font-medium',
                    isActive
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:bg-background/50 hover:text-foreground'
                  )}
                  key={name}
                  to={url}
                >
                  {content}
                </Link>
              );
            }
          )}
        </div>
      </nav>
      <div className="bg-sidebar sticky top-16 hidden h-[calc(100dvh-64px)] overflow-y-auto border-r px-3 py-12 md:block">
        <SidebarMenu>
          {SIDEBAR_MENU_ITEMS.map(
            ({ badge, isDisabled, isDisabledTooltip, name, url }) => {
              const disabled = AssertIsDisabled(name, isDisabled);
              const showTooltip =
                disabled &&
                typeof isDisabledTooltip === 'string' &&
                isDisabledTooltip.trim() !== '';

              const buttonContent = disabled ? (
                <span className="flex w-full items-center justify-between">
                  {name}
                  {renderedBadge(name, badge)}
                </span>
              ) : (
                <Link
                  className="flex items-center justify-between"
                  to={url}
                >
                  {name}
                  {renderedBadge(name, badge)}
                </Link>
              );

              return (
                <SidebarMenuItem key={name}>
                  {showTooltip ? (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <SidebarMenuButton
                          className="w-full"
                          disabled={disabled}
                          isActive={pathname.split('/')[4] === url}
                        >
                          {buttonContent}
                        </SidebarMenuButton>
                      </TooltipTrigger>
                      <TooltipContent>
                        {typeof isDisabledTooltip === 'string'
                          ? isDisabledTooltip
                          : ''}
                      </TooltipContent>
                    </Tooltip>
                  ) : (
                    <SidebarMenuButton
                      asChild={!disabled}
                      disabled={disabled}
                      isActive={pathname.split('/')[4] === url}
                    >
                      {buttonContent}
                    </SidebarMenuButton>
                  )}
                </SidebarMenuItem>
              );
            }
          )}
        </SidebarMenu>
      </div>
      <div className="h-[calc(100dvh-64px)] overflow-auto">
        <Outlet
          context={
            {
              addBreadcrumb,
              additionalBreadcrumbs,
              closingDate: data?.node.closingDate,
              companyName: data?.node.targetEntityId?.name,
              ddStage:
                dueDiligenceData?.node.dueDiligenceProjectId?.nextMilestoneId
                  ?.name ?? null,
              icVoteTimestamp: data?.node.icVoteTimestamp,
              id,
              loading,
              opportunityName: data?.node.name ?? null,
              projectName: data?.node.projectName,
              targetEntityId: data?.node.targetEntityId?.id ?? null
            } satisfies ContextType
          }
        />
      </div>
    </div>
  );
};

export default InvestmentsDirectDetails;
